import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const round2 = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const schema = yup.object({
  valajutip: yup
    .string()
    .oneOf(['AUMENTO', 'DESCUENTO'], 'Debe seleccionar un tipo válido.')
    .required('El tipo de ajuste es obligatorio.'),
  valajucan: yup
    .number()
    .typeError('La cantidad debe ser numérica.')
    .moreThan(0, 'La cantidad debe ser mayor a 0.')
    .required('La cantidad es obligatoria.'),
  valajumonuni: yup
    .number()
    .typeError('El valor unitario debe ser numérico.')
    .moreThan(0, 'El valor unitario debe ser mayor a 0.')
    .required('El valor unitario es obligatorio.'),
  valajudes: yup
    .string()
    .trim()
    .required('La descripción es obligatoria.')
    .max(500, 'La descripción no debe superar los 500 caracteres.'),
  valajufec: yup
    .date()
    .typeError('La fecha de inicio es obligatoria.')
    .required('La fecha de inicio es obligatoria.'),
  valajufecfin: yup
    .date()
    .typeError('La fecha de fin es obligatoria.')
    .required('La fecha de fin es obligatoria.')
    .min(yup.ref('valajufec'), 'La fecha de fin no puede ser menor a la fecha de inicio.'),
});

const EMPTY_VALUES = {
  valajutip: 'AUMENTO',
  valajucan: 1,
  valajumonuni: '',
  valajudes: '',
  valajufec: '',
  valajufecfin: '',
};

const TYPE_INFO = {
  AUMENTO: {
    label: 'AUMENTO',
    color: '#2e7d32',
    text: '#fff',
    description:
      'Incrementa el subtotal de la valorización. Úsalo para recargos, conceptos adicionales o regularizaciones positivas.',
    icon: <TrendingUpIcon fontSize="small" />,
  },
  DESCUENTO: {
    label: 'DESCUENTO',
    color: '#d32f2f',
    text: '#fff',
    description:
      'Reduce el subtotal de la valorización. Úsalo para penalidades, descuentos comerciales o correcciones negativas.',
    icon: <TrendingDownIcon fontSize="small" />,
  },
};

const formatCurrency = (value, symbol = 'S/') => {
  const number = Number(value || 0);
  return `${symbol} ${number.toFixed(2)}`;
};

const AdjustmentForm = ({
  open,
  handleClose,
  defaultValues = {},
  onSubmit,
  loading = false,
  currencySymbol = 'S/',
}) => {
  const isEditMode = Boolean(defaultValues?.valajuid);

  const safeDefaults = useMemo(() => {
    if (!defaultValues || Object.keys(defaultValues).length === 0) {
      return EMPTY_VALUES;
    }

    return {
      valajutip: defaultValues.valajutip || 'AUMENTO',
      valajucan:
        defaultValues.valajucan !== undefined && defaultValues.valajucan !== null
          ? Number(defaultValues.valajucan)
          : 1,
      valajumonuni:
        defaultValues.valajumonuni !== undefined && defaultValues.valajumonuni !== null
          ? Number(defaultValues.valajumonuni)
          : '',
      valajudes: defaultValues.valajudes || '',
      valajufec: defaultValues.valajufec || '',
      valajufecfin: defaultValues.valajufecfin || '',
    };
  }, [defaultValues]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: safeDefaults,
  });

  useEffect(() => {
    if (open) {
      reset(safeDefaults);
    }
  }, [open, safeDefaults, reset]);

  const watchedType = watch('valajutip');
  const watchedQuantity = watch('valajucan');
  const watchedUnitAmount = watch('valajumonuni');
  const watchedDescription = watch('valajudes');

  const typeInfo = TYPE_INFO[watchedType] || TYPE_INFO.AUMENTO;

  const calculatedAmount = useMemo(() => {
    return round2(Number(watchedQuantity || 0) * Number(watchedUnitAmount || 0));
  }, [watchedQuantity, watchedUnitAmount]);

  const internalSubmit = (formData) => {
    const payload = {
      valajutip: formData.valajutip,
      valajucan: round2(formData.valajucan),
      valajumonuni: round2(formData.valajumonuni),
      valajudes: formData.valajudes?.trim(),
      valajufec: formData.valajufec,
      valajufecfin: formData.valajufecfin,
    };

    onSubmit?.(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1.2}>
          <TuneIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {isEditMode ? 'Editar ajuste' : 'Nuevo ajuste'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define la cantidad y el valor unitario. El monto total será calculado automáticamente.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
        <Box component="form" id="adjustment-form" onSubmit={handleSubmit(internalSubmit)}>
          <Stack spacing={2.5}>
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                El monto total del ajuste se calcula como cantidad por valor unitario.
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="valajutip"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Tipo de ajuste"
                        error={!!errors.valajutip}
                        helperText={
                          errors.valajutip?.message ||
                          'Selecciona si suma o descuenta.'
                        }
                      >
                        <MenuItem value="AUMENTO">AUMENTO</MenuItem>
                        <MenuItem value="DESCUENTO">DESCUENTO</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="valajucan"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="number"
                        label="Cantidad"
                        inputProps={{ min: 0, step: '0.01' }}
                        error={!!errors.valajucan}
                        helperText={errors.valajucan?.message || 'Cantidad del ajuste.'}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="valajumonuni"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="number"
                        label={`Valor unitario (${currencySymbol})`}
                        inputProps={{ min: 0, step: '0.01' }}
                        error={!!errors.valajumonuni}
                        helperText={
                          errors.valajumonuni?.message ||
                          'Valor por unidad.'
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="valajufec"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="date"
                        label="Fecha inicio"
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.valajufec}
                        helperText={errors.valajufec?.message || 'Inicio del periodo del ajuste.'}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="valajufecfin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="date"
                        label="Fecha fin"
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.valajufecfin}
                        helperText={errors.valajufecfin?.message || 'Fin del periodo del ajuste.'}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="valajudes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        minRows={4}
                        label="Descripción / justificación"
                        placeholder="Ejemplo: Descuento por servicio no ejecutado, recargo por atención especial, ajuste comercial, etc."
                        error={!!errors.valajudes}
                        helperText={
                          errors.valajudes?.message ||
                          'Explica claramente por qué se aplica este ajuste.'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                Vista previa del ajuste
              </Typography>

              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Chip
                  icon={typeInfo.icon}
                  label={typeInfo.label}
                  sx={{
                    backgroundColor: typeInfo.color,
                    color: typeInfo.text,
                    fontWeight: 'bold',
                  }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" mb={2}>
                {typeInfo.description}
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Cantidad
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {Number(watchedQuantity || 0).toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Valor unitario
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(watchedUnitAmount || 0, currencySymbol)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Monto total
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={watchedType === 'DESCUENTO' ? 'error.main' : 'success.main'}
                  >
                    {formatCurrency(calculatedAmount, currencySymbol)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Efecto
                  </Typography>
                  <Typography
                    fontWeight="bold"
                    color={watchedType === 'DESCUENTO' ? 'error.main' : 'success.main'}
                  >
                    {watchedType === 'DESCUENTO'
                      ? 'Reducirá el subtotal'
                      : 'Incrementará el subtotal'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Descripción registrada
                  </Typography>
                  <Typography>
                    {watchedDescription?.trim() || 'Aún no se ingresó una descripción.'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CloseIcon />}
        >
          Cancelar
        </Button>

        <Button
          type="submit"
          form="adjustment-form"
          variant="contained"
          disabled={loading || (isEditMode && !isDirty)}
          startIcon={<SaveIcon />}
        >
          {loading
            ? 'Guardando...'
            : isEditMode
              ? 'Guardar cambios'
              : 'Registrar ajuste'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustmentForm;